import type { TFunction } from "i18next";
import type { DateTime } from "luxon";
import { OutreachExclusionFilter, OutreachLevel, OutreachVia, type RequestOutreach } from "../../../utils/models";
import { CALL_GAP_MINUTES } from "../../../utils/outreachRules";
import {
	CALLS_GROUP_ID,
	isAtLeast,
	isCalling,
	levelNumberOf,
	levelStartOf,
	type OutreachGroupHelpers,
	OutreachGroupIcon,
	type OutreachGroupView,
	OutreachRowAction,
} from "./outreachGroupTypes";
import type { OutreachCopy } from "./useOutreachCopy";

/** `at` orders groups in time; `null` (held or switched off) sorts them last. */
type Placed = { at: DateTime | null; group: OutreachGroupView };

const UP_NEXT_ACTIONS = [OutreachRowAction.SendNow, OutreachRowAction.Skip];
const CALL_ACTIONS = [OutreachRowAction.CallNow, OutreachRowAction.Skip];

type Ctx = { t: TFunction; outreach: RequestOutreach; now: DateTime; h: OutreachGroupHelpers; copy: OutreachCopy };

/** A note-only group: a level still to come, or one that's off. */
const noteGroup = (id: string, title: string, meta: string | undefined, icon: OutreachGroupIcon, note: string): OutreachGroupView => ({
	id,
	title,
	meta,
	icon,
	note,
	rows: [],
	countsInTab: false,
	defaultOpen: false,
});

/** One group per day of planned texts, each person on it with Text now and Skip. */
const textGroupsOf = ({ t, outreach, h }: Ctx): Placed[] =>
	outreach.plannedBatches
		.filter((batch) => batch.via === OutreachVia.Text)
		.map((batch) => ({
			at: batch.plannedAt,
			group: {
				id: batch.id,
				title: t("request.outreach.group.will.text", { people: h.people(batch.people.length) }),
				meta: h.dayMeta(batch.plannedAt),
				icon: OutreachGroupIcon.Message,
				note: undefined,
				rows: batch.people.map((person) => h.scoredRow(person, [], UP_NEXT_ACTIONS, null)),
				countsInTab: true,
				defaultOpen: false,
			},
		}));

/** The planned app alert: it goes out on its own, so its people can only be skipped. */
const alertGroupsOf = ({ t, outreach, h }: Ctx): Placed[] =>
	outreach.plannedBatches
		.filter((batch) => batch.via === OutreachVia.AppAlert)
		.map((batch) => ({
			at: batch.plannedAt,
			group: {
				id: batch.id,
				title: t("request.outreach.group.will.app_alert", { people: h.people(batch.people.length) }),
				meta: h.dayMeta(batch.plannedAt),
				icon: OutreachGroupIcon.AppAlert,
				note: t("request.outreach.group.app_alert_note"),
				rows: batch.people.map((person) => h.scoredRow(person, [], [OutreachRowAction.Skip], null)),
				countsInTab: false,
				defaultOpen: false,
			},
		}));

/**
 * The call list; while calling it's who's next and carries the actions, before that it's a preview. A preview counts
 * toward the tab only once no texts are left in line, so the number is who we ask next, not everyone twice.
 */
const callGroupsOf = ({ t, outreach, now, h }: Ctx): Placed[] => {
	if (!outreach.effort.allowed.calls) {
		return [
			{
				at: null,
				group: noteGroup(
					`${CALLS_GROUP_ID}-off`,
					t("request.outreach.calls.off.title"),
					undefined,
					OutreachGroupIcon.Call,
					t("request.outreach.calls.off.note"),
				),
			},
		];
	}
	const first = outreach.plannedCalls[0];
	if (first === undefined) return [];
	const calling = isCalling(outreach);
	return [
		{
			at: first.plannedAt,
			group: {
				id: CALLS_GROUP_ID,
				title: t("request.outreach.group.calls", { people: h.people(outreach.plannedCalls.length) }),
				meta: first.plannedAt <= now || calling ? t("request.outreach.when.now") : t("request.outreach.when.from", { when: h.dayMeta(first.plannedAt) }),
				icon: OutreachGroupIcon.Call,
				note: t("request.outreach.calls.note", {
					callsLevel: levelNumberOf(OutreachLevel.Calls),
					fastLevel: levelNumberOf(OutreachLevel.Overtime),
					steadyMinutes: CALL_GAP_MINUTES.steady,
					fastMinutes: CALL_GAP_MINUTES.fast,
				}),
				// Call now works before calls start too: it calls them ahead of the plan.
				rows: outreach.plannedCalls.map((call) => h.scoredRow(call.person, [], CALL_ACTIONS, h.rowTimeOf(call.plannedAt))),
				countsInTab: calling || !outreach.plannedBatches.some((batch) => batch.via === OutreachVia.Text),
				defaultOpen: false,
			},
		},
	];
};

/** The Overtime level still to come: who it would offer the shift to, or that it's off. */
const overtimeGroupsOf = ({ t, outreach, h, copy }: Ctx): Placed[] => {
	if (isAtLeast(outreach, OutreachLevel.Overtime)) return [];
	if (!outreach.effort.allowed.overtime) {
		return [
			{
				at: null,
				group: noteGroup(
					"overtime-off",
					t("request.outreach.overtime.off.title"),
					undefined,
					OutreachGroupIcon.Overtime,
					t("request.outreach.overtime.off.note"),
				),
			},
		];
	}
	const people = outreach.excludedGroups.filter((group) => group.filter === OutreachExclusionFilter.DailyOvertime).flatMap((group) => group.people);
	if (people.length === 0) return [];
	const at = levelStartOf(outreach, OutreachLevel.Overtime);
	const title = t("request.outreach.overtime.planned", { level: copy.levelOption(OutreachLevel.Overtime), people: h.people(people.length) });
	return [
		{
			at,
			group: {
				id: "overtime",
				title,
				meta: at === null ? t("request.outreach.when.holding") : h.dayMeta(at),
				icon: OutreachGroupIcon.Overtime,
				note: t("request.outreach.overtime.note", { number: levelNumberOf(OutreachLevel.Overtime) }),
				// Each row names the shift that puts them into overtime; Text now offers it ahead of the level.
				rows: people.map((person) => ({
					nurseId: person.nurseId,
					name: person.name,
					scored: null,
					line: copy.exclusionText(person),
					badges: [],
					when: null,
					actions: [OutreachRowAction.OfferNow],
				})),
				// They're counted on Not texting until the offer goes out.
				countsInTab: false,
				defaultOpen: false,
			},
		},
	];
};

/** The Agency level still to come, or that it's off; once posted, the post is on Reached. */
const agencyGroupsOf = ({ t, outreach, h, copy }: Ctx): Placed[] => {
	const { postedAt, source, minOpenSpots } = outreach.agency;
	if (postedAt !== null || isAtLeast(outreach, OutreachLevel.Agency)) return [];
	if (!outreach.effort.allowed.agency) {
		return [
			{
				at: null,
				group: noteGroup(
					"agency-off",
					t("request.outreach.agency_level.off.title"),
					undefined,
					OutreachGroupIcon.Agency,
					t("request.outreach.agency_level.off.note"),
				),
			},
		];
	}
	const at = levelStartOf(outreach, OutreachLevel.Agency);
	const note = t("request.outreach.agency_level.note", { count: minOpenSpots, source: t(`request.outreach.agency.${source}`) });
	return [
		{
			at,
			group: noteGroup(
				"agency",
				copy.levelOption(OutreachLevel.Agency),
				at === null ? t("request.outreach.when.holding") : h.dayMeta(at),
				OutreachGroupIcon.Agency,
				note,
			),
		},
	];
};

const byWhen = (a: Placed, b: Placed): number => {
	if (a.at === null || b.at === null) return Number(a.at === null) - Number(b.at === null);
	return a.at.toMillis() - b.at.toMillis();
};

/** Up next, in plan order: each day's texts, the app alert, the calls, then the Overtime and Agency levels still to come. */
export const upNextGroupsOf = (t: TFunction, outreach: RequestOutreach, now: DateTime, h: OutreachGroupHelpers, copy: OutreachCopy): OutreachGroupView[] => {
	const ctx: Ctx = { t, outreach, now, h, copy };
	return [...textGroupsOf(ctx), ...alertGroupsOf(ctx), ...callGroupsOf(ctx), ...overtimeGroupsOf(ctx), ...agencyGroupsOf(ctx)]
		.sort(byWhen)
		.map((placed, index) => ({ ...placed.group, defaultOpen: index === 0 }));
};
